import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface PrimaryCTAProps {
  text?: string;
  href?: string;
  external?: boolean;
  className?: string;
  fullWidth?: boolean;
  size?: 'default' | 'lg';
}

/**
 * Standardized Primary CTA Button
 * - Text: "Book Free Strategy Call" (default)
 * - Style: Orange gradient (#f97316 to #ea580c), white text, rounded-full
 * - Hover: Brighter orange + scale-up (1.02), shadow orange/25
 * - Size: h-12 px-8 text-base font-medium on desktop, full-width on mobile
 * - Icon: Right-arrow that moves right on hover
 */
export function PrimaryCTA({
  text = 'Book Free Strategy Call',
  href = 'https://calendly.com/smartsitesolutions-pm/30min',
  external = true,
  className,
  fullWidth = false,
  size = 'default',
}: PrimaryCTAProps) {
  const buttonClasses = cn(
    'primary-cta group inline-flex items-center justify-center gap-2 min-h-[44px]',
    size === 'lg' && 'h-14 px-10 text-lg',
    fullWidth && 'w-full',
    className
  );

  const content = (
    <>
      {text}
      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
    </>
  );

  if (external) {
    return (
      <Button asChild className={buttonClasses}>
        <a href={href} target="_blank" rel="noopener noreferrer">
          {content}
        </a>
      </Button>
    );
  }

  return (
    <Button asChild className={buttonClasses}>
      <Link to={href}>
        {content}
      </Link>
    </Button>
  );
}

interface SecondaryCTAProps {
  text?: string;
  href?: string;
  external?: boolean;
  className?: string;
  fullWidth?: boolean;
}

/**
 * Standardized Secondary CTA Button
 * - Style: Glass button (backdrop-blur, border-white/10, hover:bg-white/5)
 */
export function SecondaryCTA({
  text = 'Run Free Mobile Speed Check',
  href = 'https://pagespeed.web.dev/',
  external = true,
  className,
  fullWidth = false,
}: SecondaryCTAProps) {
  const buttonClasses = cn(
    'glass-button group inline-flex items-center justify-center gap-2 h-12 px-8 text-base font-medium min-h-[44px]',
    fullWidth && 'w-full',
    className
  );

  const content = (
    <>
      {text}
      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
    </>
  );

  if (external) {
    return (
      <Button asChild variant="ghost" className={buttonClasses}>
        <a href={href} target="_blank" rel="noopener noreferrer">
          {content}
        </a>
      </Button>
    );
  }

  return (
    <Button asChild variant="ghost" className={buttonClasses}>
      <Link to={href}>
        {content}
      </Link>
    </Button>
  );
}
